import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';

/** Match server GAME_SUGGESTION_TEXT_RE (letters, numbers, spaces, ?). Used for vastaus / vaihtoehdot. */
const GAME_SUGGESTION_TEXT_RE = /^[\p{L}\p{N}\s?]*$/u;

/** Kysymys: only ; < > forbidden (match server). */
const GAME_SUGGESTION_QUESTION_FORBIDDEN_RE = /[;<>]/;

function titleForGameType(gt) {
  switch (String(gt || '').toLowerCase()) {
    case 'trivia':
      return 'Ehdota trivia-kysymystä';
    case 'riddle':
      return 'Ehdota arvoitus';
    case 'abcd':
      return 'Ehdota ABCD-kysymystä';
    case 'emoji':
      return 'Ehdota emoji-kysymystä';
    default:
      return 'Peliehdotus';
  }
}

export default function TriviaSuggestionModal({ user, suggestionId, onClose, onSubmitted }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [meta, setMeta] = useState(null);

  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [htmlField, setHtmlField] = useState('');
  const [optA, setOptA] = useState('');
  const [optB, setOptB] = useState('');
  const [optC, setOptC] = useState('');
  const [optD, setOptD] = useState('');
  const [correctAbcd, setCorrectAbcd] = useState('A');

  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  const userIdStr = user ? String(user.id).replace(/n$/, '') : '';

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  useEffect(() => {
    if (!userIdStr || !suggestionId) return;
    setLoading(true);
    setLoadError(null);
    fetch(
      `${API}/api/game-suggestions/${encodeURIComponent(suggestionId)}?userId=${encodeURIComponent(userIdStr)}`,
      {
        headers: { 'Content-Type': 'application/json' },
      },
    )
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || data.message || 'Virhe');
        return data;
      })
      .then((data) => {
        setMeta(data);
        setQuestion(data.question || '');
        setAnswer(data.answer || '');
        setHtmlField(data.htmlContent || '');
        const o = data.abcdOptions || {};
        setOptA(o.A || '');
        setOptB(o.B || '');
        setOptC(o.C || '');
        setOptD(o.D || '');
        if (['A', 'B', 'C', 'D'].includes(String(data.answer || '').toUpperCase())) {
          setCorrectAbcd(String(data.answer).toUpperCase());
        }
        setAlreadySubmitted(!!data.submitted);
      })
      .catch((e) => setLoadError(e.message || 'Virhe'))
      .finally(() => setLoading(false));
  }, [userIdStr, suggestionId]);

  if (!user) return null;

  const validateQuestion = (label, value, required, maxLen) => {
    const t = String(value || '').trim();
    if (required && !t) return `${label}: täytä kenttä.`;
    if (t.length > maxLen) return `${label}: liian pitkä.`;
    if (t && GAME_SUGGESTION_QUESTION_FORBIDDEN_RE.test(t))
      return `${label}: merkit ; < > eivät ole sallittuja.`;
    return null;
  };

  const validateAlnum = (label, value, required, maxLen) => {
    const t = String(value || '').trim();
    if (required && !t) return `${label}: täytä kenttä.`;
    if (t.length > maxLen) return `${label}: liian pitkä.`;
    if (t && !GAME_SUGGESTION_TEXT_RE.test(t))
      return `${label}: vain kirjaimet, numerot, välilyönti ja kysymysmerkki (?).`;
    return null;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setError(null);
    const gt = String(meta?.gameType || '').toLowerCase();

    let body;
    if (gt === 'trivia' || gt === 'riddle') {
      const qe = validateQuestion('Kysymys', question, true, 2000);
      const ae = validateAlnum('Vastaus', answer, true, 500);
      if (qe || ae) {
        setError(qe || ae);
        return;
      }
      body = { question: question.trim(), answer: answer.trim() };
    } else if (gt === 'abcd') {
      const qe = validateQuestion('Kysymys', question, true, 2000);
      const ea = validateAlnum('Vaihtoehto A', optA, true, 500);
      const eb = validateAlnum('Vaihtoehto B', optB, true, 500);
      const ec = validateAlnum('Vaihtoehto C', optC, true, 500);
      const ed = validateAlnum('Vaihtoehto D', optD, true, 500);
      const first = qe || ea || eb || ec || ed;
      if (first) {
        setError(first);
        return;
      }
      body = {
        question: question.trim(),
        optionA: optA.trim(),
        optionB: optB.trim(),
        optionC: optC.trim(),
        optionD: optD.trim(),
        correctOption: correctAbcd,
      };
    } else if (gt === 'emoji') {
      const h = String(htmlField || '').trim();
      const q = String(question || '').trim();
      const a = String(answer || '').trim();
      if (!h || h.length > 8000) {
        setError('HTML-kenttä puuttuu tai on liian pitkä.');
        return;
      }
      const qe = validateQuestion('Kysymys', question, true, 2000);
      if (qe) {
        setError(qe);
        return;
      }
      if (!a || a.length > 500) {
        setError('Vastaus puuttuu tai on liian pitkä.');
        return;
      }
      body = { html: h, question: q, answer: a };
    } else {
      setError('Tuntematon pelityyppi.');
      return;
    }

    setSaving(true);
    fetch(`${API}/api/game-suggestions/${encodeURIComponent(suggestionId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, userId: userIdStr }),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || data.message || 'Virhe');
        return data;
      })
      .then(() => setSent(true))
      .catch((err) => setError(err.message || 'Virhe'))
      .finally(() => setSaving(false));
  };

  const gtLower = String(meta?.gameType || '').toLowerCase();
  const modalTitle = titleForGameType(meta?.gameType);

  return (
    <div className="Modal-backdrop TriviaSuggestionModal-backdrop" role="presentation">
      <div
        className="Modal TriviaSuggestionModal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="trivia-suggestion-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="trivia-suggestion-modal-title">{modalTitle}</h2>

        {loading ? <p className="TriviaSuggestionModal-lead">Ladataan…</p> : null}
        {loadError ? (
          <>
            <p className="TriviaSuggestionModal-error" role="alert">
              {loadError}
            </p>
            <div className="Modal-actions">
              <button type="button" className="Button" onClick={onClose}>
                Peruuta
              </button>
            </div>
          </>
        ) : null}

        {!loading && !loadError && sent ? (
          <>
            <p className="TriviaSuggestionModal-lead" style={{ whiteSpace: 'pre-line' }}>
              Kiitos!{'\n'}
              Ehdotuksesi on vastaanotettu ja odottaa hyväksyntää.
            </p>
            <div className="Modal-actions">
              <button
                type="button"
                className="Button"
                onClick={() => {
                  if (typeof onSubmitted === 'function') onSubmitted();
                }}
              >
                OK
              </button>
            </div>
          </>
        ) : null}

        {!loading && !loadError && alreadySubmitted && !sent ? (
          <>
            <p className="TriviaSuggestionModal-lead">Olet jo lähettänyt tämän ehdotuksen.</p>
            <div className="Modal-actions">
              <button type="button" className="Button" onClick={onClose}>
                Peruuta
              </button>
            </div>
          </>
        ) : null}

        {!loading && !loadError && !alreadySubmitted && !sent ? (
          <>
            <p className="TriviaSuggestionModal-lead">
              Täytä lomake ja lähetä ehdotus. Yksi lähetys tälle ostolle.
            </p>

            <form onSubmit={onSubmit}>
              {(gtLower === 'trivia' || gtLower === 'riddle') && (
                <>
                  <label>
                    Kysymys
                    <textarea
                      className="TriviaSuggestionModal-textarea"
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      rows={4}
                      maxLength={2000}
                      required
                      disabled={saving}
                    />
                  </label>
                  <label>
                    Vastaus
                    <input
                      type="text"
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      maxLength={500}
                      required
                      disabled={saving}
                    />
                  </label>
                </>
              )}

              {gtLower === 'abcd' && (
                <>
                  <label>
                    Kysymys
                    <textarea
                      className="TriviaSuggestionModal-textarea"
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      rows={3}
                      maxLength={2000}
                      required
                      disabled={saving}
                    />
                  </label>
                  <label>
                    Vaihtoehto A
                    <input
                      type="text"
                      value={optA}
                      onChange={(e) => setOptA(e.target.value)}
                      maxLength={500}
                      required
                      disabled={saving}
                    />
                  </label>
                  <label>
                    Vaihtoehto B
                    <input
                      type="text"
                      value={optB}
                      onChange={(e) => setOptB(e.target.value)}
                      maxLength={500}
                      required
                      disabled={saving}
                    />
                  </label>
                  <label>
                    Vaihtoehto C
                    <input
                      type="text"
                      value={optC}
                      onChange={(e) => setOptC(e.target.value)}
                      maxLength={500}
                      required
                      disabled={saving}
                    />
                  </label>
                  <label>
                    Vaihtoehto D
                    <input
                      type="text"
                      value={optD}
                      onChange={(e) => setOptD(e.target.value)}
                      maxLength={500}
                      required
                      disabled={saving}
                    />
                  </label>
                  <label>
                    Vastaus
                    <select
                      value={correctAbcd}
                      onChange={(e) => setCorrectAbcd(e.target.value)}
                      disabled={saving}
                    >
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                    </select>
                  </label>
                </>
              )}

              {gtLower === 'emoji' && (
                <>
                  <label>
                    HTML
                    <textarea
                      className="TriviaSuggestionModal-textarea"
                      value={htmlField}
                      onChange={(e) => setHtmlField(e.target.value)}
                      rows={3}
                      maxLength={8000}
                      required
                      disabled={saving}
                    />
                  </label>
                  <label>
                    Kysymys
                    <textarea
                      className="TriviaSuggestionModal-textarea"
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      rows={2}
                      maxLength={2000}
                      required
                      disabled={saving}
                    />
                  </label>
                  <label>
                    Vastaus
                    <input
                      type="text"
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      maxLength={500}
                      required
                      disabled={saving}
                    />
                  </label>
                </>
              )}

              {error ? (
                <p className="TriviaSuggestionModal-error" role="alert">
                  {error}
                </p>
              ) : null}

              <div className="Modal-actions">
                <button type="button" className="Button" onClick={onClose} disabled={saving}>
                  Peruuta
                </button>
                <button type="submit" className="Button" disabled={saving}>
                  {saving ? 'Lähetetään…' : 'Lähetä'}
                </button>
              </div>
            </form>
          </>
        ) : null}
      </div>
    </div>
  );
}
